import React, { useState, useEffect } from 'react';
import { templatesAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileCode, Plus, Edit2, Trash2, Copy } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['general', 'breaking', 'opinion', 'feature', 'interview', 'review'];

export default function AdminTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', category: 'general', content_template: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try { const res = await templatesAPI.list(); setTemplates(res.data.templates || []); }
    catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    try {
      if (editing) { await templatesAPI.update(editing.id, form); toast.success('Updated'); }
      else { await templatesAPI.create(form); toast.success('Created'); }
      setShowDialog(false); setEditing(null);
      setForm({ name: '', description: '', category: 'general', content_template: '' });
      loadData();
    } catch { toast.error('Failed'); }
  };

  const deleteTemplate = async (id) => {
    try { await templatesAPI.delete(id); toast.success('Deleted'); loadData(); }
    catch { toast.error('Failed'); }
  };

  const copyTemplate = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Template copied');
  };

  return (
    <div className="space-y-6" data-testid="admin-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileCode className="w-6 h-6 text-[#B89B5E]" /> Content Templates</h1>
          <p className="text-sm text-[#5B616E] mt-1">Reusable article templates for consistent content</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ name: '', description: '', category: 'general', content_template: '' }); setShowDialog(true); }} data-testid="create-template-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> New Template</Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
      ) : templates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map(t => (
            <Card key={t.id} className="bg-white border-[#E7E0D6]" data-testid="template-card">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="outline" className="text-[10px] capitalize">{t.category}</Badge>
                    <h3 className="font-medium mt-2">{t.name}</h3>
                    <p className="text-xs text-[#5B616E] mt-1">{t.description || 'No description'}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => copyTemplate(t.content_template)}><Copy className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditing(t); setForm({ name: t.name, description: t.description || '', category: t.category, content_template: t.content_template || '' }); setShowDialog(true); }}><Edit2 className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#B3261E]" onClick={() => deleteTemplate(t.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
                {t.content_template && (
                  <div className="mt-3 pt-3 border-t border-[#E7E0D6]">
                    <pre className="text-xs text-[#5B616E] whitespace-pre-wrap line-clamp-3 font-mono">{t.content_template}</pre>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-[#5B616E]">
          <FileCode className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>No templates yet</p>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit Template' : 'New Template'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Name</label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="template-name-input" /></div>
            <div><label className="text-sm font-medium mb-1 block">Category</label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Description</label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div><label className="text-sm font-medium mb-1 block">Template Content (Markdown)</label><Textarea value={form.content_template} onChange={e => setForm({ ...form, content_template: e.target.value })} rows={8} className="font-mono text-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#121316] text-white hover:bg-[#1B1D22]">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
